import { useEffect, useRef, useState } from "react";

export default function Customizations() {
  const methods = [
    {
      title: "DTF Printing",
      desc: "Durable, vivid colours suitable for both small runs and bulk orders. Ideal when flexibility and detail are equally important.",
    },
    {
      title: "Screen Printing",
      desc: "Refined, cost-effective printing trusted for large-scale production where consistency matters most.",
    },
    {
      title: "Embroidery",
      desc: "Thread-based detailing that adds a premium, tactile finish — often chosen for uniforms and long-term wear.",
    },
  ];

  const headerRef = useRef(null);
  const methodsRef = useRef(null);

  const [headerVisible, setHeaderVisible] = useState(false);
  const [methodsVisible, setMethodsVisible] = useState(false);

  useEffect(() => {
    const headerObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeaderVisible(true);
          headerObserver.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    const methodsObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMethodsVisible(true);
          methodsObserver.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    if (headerRef.current) headerObserver.observe(headerRef.current);
    if (methodsRef.current) methodsObserver.observe(methodsRef.current);

    return () => {
      headerObserver.disconnect();
      methodsObserver.disconnect();
    };
  }, []);

  return (
    <section className="relative bg-[var(--bg-main)] text-[var(--text-main)] py-24 px-6">

      {/* subtle ambient warmth */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.035]
                   bg-[radial-gradient(circle_at_center,#b8924a,transparent_75%)]"
      />

      <div className="max-w-6xl mx-auto relative z-10">

        {/* HEADER — SLIDES FROM RIGHT */}
        <div className="grid md:grid-cols-2 mb-28">
          <div />

          <div
            ref={headerRef}
            className={`
              max-w-xl
              transition-all duration-[1900ms]
              ease-[cubic-bezier(0.22,1,0.36,1)]
              ${headerVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-44"}
            `}
          >
            <p className="uppercase tracking-[0.28em] text-xs text-[#8a8a8a] mb-4">
              Customisation
            </p>

            <h2 className="text-5xl font-serif tracking-tight mb-6">
              Printing, chosen with intent
            </h2>

            <p className="text-[var(--text-muted)] leading-relaxed">
              Every fabric and design responds differently to print.
              We help you select a method that supports durability,
              clarity, and long-term wear.
            </p>
          </div>
        </div>

        {/* METHODS — PARTICLE / ASH FORMATION */}
        <div
          ref={methodsRef}
          className={`space-y-24 ${methodsVisible ? "ash-visible" : ""}`}
        >
          {methods.map((item, i) => (
            <div
              key={item.title}
              className="method-step grid md:grid-cols-2"
              style={{ animationDelay: `${i * 0.25}s` }}
            >
              {/* left negative space */}
              <div />

              {/* right content */}
              <div className="grid grid-cols-[70px_1fr] gap-8 items-start">

                {/* marker */}
                <div className="relative">
                  <div className="w-9 h-9 rounded-full border border-[#b8924a]/50 method-content" />

                  {i !== methods.length - 1 && (
                    <div className="absolute top-11 left-[18px] h-[80px] w-px bg-black/10" />
                  )}
                </div>

                {/* text */}
                <div className="max-w-xl method-content">
                  <h3 className="text-2xl font-serif mb-3">
                    {item.title}
                  </h3>

                  <p className="text-[var(--text-muted)] leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA — unchanged */}
        <div className="grid md:grid-cols-2 mt-28">
          <div />

          <a
            href="/customizations"
            className="
              inline-flex items-center gap-3
              border-b border-black/30 pb-2
              text-lg tracking-wide
              hover:border-[#b8924a]
              hover:text-[#b8924a]
              transition
            "
          >
            Learn more about customisation
            <span className="text-xl">→</span>
          </a>
        </div>
      </div>

      {/* PARTICLE / ASH STYLES — SAME LOGIC AS HOW IT WORKS */}
      <style>{`
        .method-step {
          position: relative;
          opacity: 0;
        }

        .ash-visible .method-step {
          animation: ashReveal 1.8s ease forwards;
        }

        .method-step::before {
          content: "";
          position: absolute;
          inset: -12px;
          background:
            radial-gradient(circle at 30% 30%, rgba(0,0,0,0.07), transparent 45%),
            radial-gradient(circle at 70% 55%, rgba(0,0,0,0.06), transparent 50%),
            radial-gradient(circle at 50% 85%, rgba(0,0,0,0.05), transparent 55%);
          opacity: 0;
          filter: blur(3px);
          pointer-events: none;
          animation: ashParticles 1.6s ease forwards;
        }

        .method-content {
          opacity: 0;
          transform: translateY(6px);
          animation: contentSettle 1.2s ease forwards;
          animation-delay: 0.4s;
        }

        .ash-visible .method-content,
        .ash-visible .method-step::before {
          animation-play-state: running;
        }

        @keyframes ashReveal {
          to { opacity: 1; }
        }

        @keyframes ashParticles {
          0% {
            opacity: 0;
            transform: translateY(10px) scale(0.97);
          }
          40% { opacity: 1; }
          100% {
            opacity: 0;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes contentSettle {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}
